/**
 * Shared Manager+ role predicate (SE-6936).
 *
 * One predicate, consumed by every surface that must agree on "may this user
 * see Manager+ material": the nav item, the route layout, and the Home
 * widget. Before this existed, Squad expressed the same policy three times as
 * reads of the PostHog `squad-role-gating` flag, which is a rollout toggle
 * with its targeting configured in a dashboard — not an authorization check.
 * A MEMBER for whom the flag resolved `true` was admitted by all three.
 *
 * Fails CLOSED. `undefined` (identity in flight), `null` (signed out) and a
 * missing role all return `false`, mirroring `useIsPipelineManager`. A gate
 * that fails open during hydration is a gate that leaks on every cold load.
 *
 * This is affordance-hiding and defense-in-depth ONLY. It hides UI and stops
 * this client issuing the queries; it cannot make the API refuse anyone. The
 * authoritative gate must live in the resolver — see SE-6936's finding that
 * every squad query answers a MEMBER bearer token in full.
 * @module hooks/useIsManagerPlus
 */

import { MANAGER_PLUS_ROLES } from "@/constants/managerPlusRoles";
import { useCurrentUser } from "@/features/auth/providers/CurrentUser";

/**
 * Returns whether the current user holds Owner, Admin, or Manager.
 * @returns `true` only for a resolved Manager+ user; `false` for MEMBER, a
 * missing role, signed-out, and the loading window (fail-closed).
 */
function useIsManagerPlus(): boolean {
  const currentUser = useCurrentUser();
  const roleName = currentUser?.role?.name;
  return roleName !== undefined && MANAGER_PLUS_ROLES.has(roleName);
}

export default useIsManagerPlus;
